import React, { useState, useEffect, useRef } from 'react'
import '../sidebar/sidebar.scss'
import LinkIcon from '../../../shared/components/svgIcons/LinkIcon'
import MembersShown from './MembersShown'
import MembersOffIcon from './MembersOffIcon'
import EditIcon from '../../../shared/components/svgIcons/EditIcon'
import InfoButton from './InfoButton'
import { useParams } from 'react-router-dom'
import api from '../../../api'
import { useWorkspace } from '../WorkspaceContext'
import CopyIcon from '../../../shared/components/svgIcons/CopyIcon'
import PermCheckIcon from '../../../shared/components/svgIcons/PermCheckIcon'
import ReadOnlyIcon from '../../../shared/components/svgIcons/ReadOnlyIcon'


const SettingsTab = () => {
    const { roomSettings, setRoomDetails, membersList, setRoomSettingsInfoPopup, ctheme } = useWorkspace();

    const [shareableLink, setShareableLink] = useState(roomSettings.shareableLinkEnabled);
    const [trackMembers, setTrackMembers] = useState(roomSettings.trackMembersEnabled);
    const [maxMembersCheck, setMaxMembersCheck] = useState(roomSettings.maxMembersEnabled);
    const [maxMembers, setMaxMembers] = useState<number | null>(roomSettings.maxMembers);
    const [defaultMemberRole, setDefaultMemberRole] = useState<'member' | 'editor'>(roomSettings.defaultMemberRole);
    const [copyLinkNoty, setCopyLinkNoty] = useState(false);
    const [saveStatus, setSaveStatus] = useState('');
    const [savingSettings, setSavingSettings] = useState(false);

    const clampMaxMembers = (value: number) => Math.min(Math.max(value, 1), 100);

    const setMaxMembersCheckClick = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        setMaxMembersCheck(e.currentTarget.checked);

        if (!maxMembersCheck) {
            setMaxMembers(roomSettings.maxMembers ?? 30);
        } else {
            setMaxMembers(null);
        }
    }

    type roleOptions = 'member' | 'editor';

    const defaultMemberRoleClick = (role: roleOptions) => {
        setDefaultMemberRole(role);
    }

    const linkRef = useRef<HTMLSpanElement | null>(null);


    const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    async function copyToClipboard() {
        try {
            if (linkRef.current) {
                await navigator.clipboard.writeText(linkRef.current.innerText);

                setCopyLinkNoty(true);

                if (copyTimeoutRef.current) {
                    clearTimeout(copyTimeoutRef.current);
                }

                copyTimeoutRef.current = setTimeout(() => {
                    setCopyLinkNoty(false);
                }, 2000);
            }
        } catch (err) {
        }
    }



    const setTrackMembersClick = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        setTrackMembers(e.currentTarget.checked);
    }

    const setShareableLinkClick = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        setShareableLink(e.currentTarget.checked);
    };

    const { roomId } = useParams();
    const shareableRoomUrl = roomId ? `${window.location.origin}/room/${roomId}` : '';

    useEffect(() => {
        setShareableLink(roomSettings.shareableLinkEnabled);
        setTrackMembers(roomSettings.trackMembersEnabled);
        setMaxMembersCheck(roomSettings.maxMembersEnabled);
        setMaxMembers(roomSettings.maxMembers);
        setDefaultMemberRole(roomSettings.defaultMemberRole);
    }, [roomSettings]);

    const hasUnsavedChanges =
        shareableLink !== roomSettings.shareableLinkEnabled ||
        trackMembers !== roomSettings.trackMembersEnabled ||
        maxMembersCheck !== roomSettings.maxMembersEnabled ||
        (maxMembersCheck && (maxMembers ?? null) !== (roomSettings.maxMembers ?? null)) ||
        defaultMemberRole !== roomSettings.defaultMemberRole;

    const hasJoinedMembers = membersList.length > 1;
    const canEditTrackMembers = roomSettings.trackMembersEnabled || !hasJoinedMembers;

    const resetRoomSettingsDraft = () => {
        setShareableLink(roomSettings.shareableLinkEnabled);
        setTrackMembers(roomSettings.trackMembersEnabled);
        setMaxMembersCheck(roomSettings.maxMembersEnabled);
        setMaxMembers(roomSettings.maxMembers);
        setDefaultMemberRole(roomSettings.defaultMemberRole);
        setSaveStatus('');
    };

    const saveRoomSettings = async () => {
        if (!roomId || savingSettings || !hasUnsavedChanges) return;

        try {
            setSavingSettings(true);
            setSaveStatus('');

            const maxMemberDraft = Number.isFinite(maxMembers) ? maxMembers : 1;
            const normalizedMaxMembers =
                maxMembersCheck
                    ? Math.min(Math.max(maxMemberDraft ?? 1, 1), 100)
                    : null;

            const { data } = await api.patch(`/rooms/${roomId}`, {
                shareableLinkEnabled: shareableLink,
                trackMembersEnabled: trackMembers,
                maxMembersEnabled: maxMembersCheck,
                maxMembers: normalizedMaxMembers,
                defaultMemberRole,
            });

            setRoomDetails(prev => prev ? {
                ...prev,
                shareableLinkEnabled: data.shareableLinkEnabled ?? null,
                trackMembersEnabled: data.trackMembersEnabled ?? null,
                maxMembersEnabled: data.maxMembersEnabled ?? null,
                maxMembers: data.maxMembers ?? null,
                defaultMemberRole: data.defaultMemberRole ?? null,
            } : prev);

            setSaveStatus('Saved');
        } catch (error) {
            setSaveStatus('Unable to save');
        } finally {
            setSavingSettings(false);
        }
    };

    return (
        <div className='sb-tab-flx-cont relative'>

            {copyLinkNoty &&
                (
                    <div className='copy-link-noty'>
                        Link Copied to Clipboard
                    </div>
                )
            }

            <div id='mode-settings'>
                <div id='ms-outline' className='sb-mode-c'>
                    <div id='mode-r-top'>
                        <h3>Room Settings</h3>

                        <div
                            className='info-btn default-btn-1'
                            onClick={() => setRoomSettingsInfoPopup(true)}
                            role='button'
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setRoomSettingsInfoPopup(true);
                                }
                            }}
                            aria-label='Info'
                            title='Info'
                        >
                            <InfoButton />
                        </div>
                    </div>
                    <div id='room-settings-tab-content'>

                        <div className="rst-pill">
                            <div className="rst-pill__label">
                                <div className="rst-i-h">
                                    <LinkIcon />
                                </div>
                                <div className='rst-p-1'>
                                    <span>Shareable Link</span>

                                    <p className='setting-status-lbl'>{shareableLink ? 'Enabled' : 'Off'}</p>
                                </div>

                            </div>

                            <input
                                type="checkbox"
                                className="theme-checkbox"
                                aria-label="Enable shareable link"
                                onChange={setShareableLinkClick}
                                checked={shareableLink}
                            />
                        </div>

                        {shareableLink && (
                            <div className='rst-pill shareable-link'>
                                <div onClick={copyToClipboard} className="rst-i-h sb-copy-btn">
                                    <CopyIcon />
                                </div>

                                <span ref={linkRef}>{shareableRoomUrl}</span>
                            </div>
                        )}



                        <div className="rst-pill">
                            <div className="rst-pill__label">
                                <div className="rst-i-h">
                                    <svg
                                        viewBox="0 0 198.61 177.3"
                                        xmlns="http://www.w3.org/2000/svg"
                                        className='mem-icon-settings'
                                    >
                                        <circle cx="99.31" cy="55.45" r="55.45" />
                                        <path d="M99.31,118.37C44.46,118.37,0,143.98,0,175.57h0c.02.58.06,1.16.11,1.73l198.42-.67c.03-.3.06-.61.08-.91v-.34c-.18-31.52-44.58-57.01-99.3-57.01h0Z" />
                                    </svg>
                                </div>

                                <div className='rst-p-1'>
                                    <span>Track Members</span>

                                    <p className='setting-status-lbl'>{trackMembers ? 'On' : 'Off'}</p>
                                </div>


                            </div>

                            <input
                                type="checkbox"
                                className="theme-checkbox"
                                aria-label="Enable member tracking"
                                onChange={setTrackMembersClick}
                                checked={trackMembers}
                                disabled={!canEditTrackMembers}
                            />


                        </div>


                        <div className="rst-pill">
                            <div className="rst-pill__label">
                                <div className="rst-i-h">
                                    <img draggable={false} src={`${process.env.PUBLIC_URL}/images/${ctheme === 'dark' ? 'numusers2' : 'numusers3'}.png`} width='15px' />
                                </div>




                                <div className='rst-p-1'>
                                    <span>Max Members</span>

                                    <p className='setting-status-lbl'>{maxMembersCheck ? 'On' : 'Off'}</p>
                                </div>
                            </div>

                            <input
                                type="checkbox"
                                className="theme-checkbox"
                                aria-label="Enable member limit"
                                checked={maxMembersCheck}
                                onChange={setMaxMembersCheckClick}
                            />
                        </div>

                        {maxMembersCheck && (
                            <div className="rst-pill">
                                <div className="max-num-input">
                                    <input
                                        type="number"
                                        value={Number.isFinite(maxMembers) ? maxMembers ?? '' : ''}
                                        min={1}
                                        max={100}
                                        onChange={(e) => {
                                            const nextValue = e.currentTarget.valueAsNumber;
                                            setMaxMembers(Number.isFinite(nextValue) ? clampMaxMembers(nextValue) : null);
                                        }}
                                        onBlur={() => setMaxMembers(prev => clampMaxMembers(Number.isFinite(prev) ? prev ?? 1 : 1))}
                                    />

                                    <div className="max-num-input__controls">
                                        <button
                                            type="button"
                                            onClick={() => setMaxMembers(prev => clampMaxMembers((Number.isFinite(prev) ? prev ?? 1 : 1) + 1))}
                                        >
                                            ▲
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() => setMaxMembers(prev => clampMaxMembers((Number.isFinite(prev) ? prev ?? 1 : 1) - 1))}
                                        >
                                            ▼
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}



                        <div className="rst-pill">
                            <div className="rst-pill__label">
                                <div className="rst-i-h">
                                    <PermCheckIcon />
                                </div>



                                <div className='rst-p-1'>
                                    <span>Default Member Role</span>

                                    <p className='setting-status-lbl'>{defaultMemberRole === 'member' ? 'Read Only' : 'Editor'}</p>
                                </div>
                            </div>

                            <div className='quick-flex'>
                                <div onClick={() => defaultMemberRoleClick('member')} className={`mjo ${defaultMemberRole === 'member' ? 'mjo-a' : ''}`}>
                                    <ReadOnlyIcon />
                                </div>

                                <div onClick={() => defaultMemberRoleClick('editor')} className={`mjo ${defaultMemberRole === 'editor' ? 'mjo-a' : ''}`}>
                                    <EditIcon />
                                </div>
                            </div>
                        </div>


                        {hasUnsavedChanges && (
                            <div className='rst-save-row'>
                                {saveStatus && <span>{saveStatus}</span>}
                                <button
                                    type='button'
                                    className='default-btn-1 rst-cancel-btn'
                                    disabled={savingSettings}
                                    onClick={resetRoomSettingsDraft}
                                >
                                    Cancel
                                </button>
                                <button
                                    type='button'
                                    className='default-btn-1'
                                    disabled={savingSettings}
                                    onClick={saveRoomSettings}
                                >
                                    {savingSettings ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        )}

                    </div>
                </div>
            </div>

        </div>
    )
}

export default SettingsTab
